import { useEffect, useRef, useState } from 'react';
import { Flame, ImageOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { MediaSticker, MediaStickerPack } from '../api';
import { matchesSticker } from '../stickerSearch';
import { stickerMutationError } from '../stickerOrder';
import { sortStickersInGroup, stickerBelongsToGroup } from '../stickerGroups';

export function StickerTabIconDialog({
  pack,
  stickers,
  onClose,
  onSave,
}: {
  pack: MediaStickerPack | null;
  stickers: MediaSticker[];
  onClose: () => void;
  onSave: (id: string | null) => Promise<void>;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastPackId = useRef<string | null>(null);
  useEffect(() => {
    if (pack && pack.packId !== lastPackId.current) {
      setSelected(pack.iconStickerId ?? null);
      setQuery('');
      setError(null);
    }
    lastPackId.current = pack?.packId ?? null;
  }, [pack]);
  const allActive = sortStickersInGroup(
    stickers.filter(
      (sticker) =>
        stickerBelongsToGroup(sticker, pack?.packId ?? null) && sticker.status === 'ACTIVE',
    ),
    pack?.packId ?? '',
    stickers,
  );
  const active = allActive.filter((sticker) => !!sticker.thumbnailUrl);
  const defaultThumbnail = allActive[0]?.thumbnailUrl;
  const results = active.filter((sticker) => !!pack && matchesSticker(sticker, query, [pack]));
  const changed = selected !== (pack?.iconStickerId ?? null);
  const selectionValid =
    selected === null || active.some((sticker) => sticker.stickerId === selected);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(selected);
      onClose();
    } catch (reason) {
      setError(stickerMutationError(reason));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!pack} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="max-w-2xl" closeDisabled={busy}>
        <DialogHeader>
          <DialogTitle>Tab icon for {pack?.displayName}</DialogTitle>
          <DialogDescription>
            Choose an active sticker in this group. The selected icon stays independent of sticker
            order.
          </DialogDescription>
        </DialogHeader>
        <Button
          variant={selected === null ? 'default' : 'outline'}
          aria-pressed={selected === null}
          disabled={busy}
          onClick={() => setSelected(null)}
        >
          {pack?.packId === 'trending' ? (
            <Flame className="size-5" />
          ) : defaultThumbnail ? (
            <img
              src={defaultThumbnail}
              alt="Default tab icon preview"
              className="size-9 object-contain"
            />
          ) : (
            <ImageOff aria-label="No default thumbnail" className="size-5" />
          )}
          Use default icon
        </Button>
        <p className="text-xs text-muted-foreground">
          {pack?.packId === 'trending'
            ? 'Default uses a Flame icon. Choose a sticker to override it.'
            : "Default uses the first active sticker in this group's saved order. Choose a sticker to keep the icon fixed."}
        </p>
        <Input
          aria-label="Search tab icon stickers"
          placeholder="Search this group's active stickers"
          value={query}
          disabled={busy}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div
          className="grid max-h-[50vh] grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2 overflow-y-auto"
          role="group"
          aria-label="Active sticker choices"
        >
          {results.map((sticker) => (
            <button
              key={sticker.stickerId}
              type="button"
              aria-label={`Choose ${sticker.name}`}
              aria-pressed={selected === sticker.stickerId}
              disabled={busy}
              onClick={() => setSelected(sticker.stickerId)}
              className={`rounded-md border p-3 text-center ${selected === sticker.stickerId ? 'border-primary bg-primary/5' : ''}`}
            >
              {sticker.thumbnailUrl && (
                <img
                  src={sticker.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="mx-auto h-20 w-full object-contain"
                />
              )}
              <span className="mt-2 block truncate text-xs">{sticker.name}</span>
            </button>
          ))}
        </div>
        {!results.length && (
          <p className="text-sm text-muted-foreground">No active stickers match.</p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy || !changed || !selectionValid} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save icon'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
